import * as React from 'react';

// interfaces
import { isEmpty } from 'lodash';
import PhoneInput from 'react-phone-input-2';
import MaskInput from 'react-maskinput';
import { Tooltip } from 'primereact/tooltip';
import { DateRange, IModal, TableColum } from './interfaces';
import { Dictionary } from '../../../types/Dictionary';
import { IControlError } from '../../../core/Controls';

// components
import { Description } from './GridComponents';
import GridMultipleFiles from './GridComponents/GridMultipleFiles';
import GridCalendar from './GridComponents/GridCalendar';
import DynamicControl from './GridComponents/DynamicControl';
import Dots from '../../../core/Dots';
import { FilesControl } from '../Files/Files';

// libs
// helpers
import {
  dateFormat,
  phoneFix,
  validateControl,
} from '../../../core/validation/validate';
import {
  cn,
  createCounterMessage,
  formatterPhone,
  getRandomKey,
  numberToFixedString,
  parseFloatCommaDot,
  trimString,
  getDigits,
  getEntries,
  valueObjectToArray,
  Guid,
} from '../../helpers/Utils';

// static
import { COUNTRIES_CODE } from '../../constants';
import { SelectBlock } from '../SelectBlock';
import GridComponentAdapter from './GridComponentAdapter';
import { DropBox } from '../../../shared/ui/dropbox/DropBox';
import { TextBox } from '../TextBox';
import RuleManagers from '../../../core/validation/RulesManager';
import { WorkExperienceList } from '../WorkExperienceList';
import { urlBuilder } from '../../../shared/utils/urlBuilder';

interface EditorModalTableProps extends IModal {
  onModalClose: () => void;
  opts: any;
  mode: string;
  value?: any;
  copiedValues?: {
    [key: string]: string;
  };
  hiddenColumns?: Array<TableColum>;
}

interface EditorModalTableState {
  validateState: Record<string, string>;
  validateStateSuccess: Record<string, string>;
  // TODO: abstract type any
  values?: any;
  hiddenColumns?: Array<TableColum>;
  isFormLocked: boolean;
}

class EditorModalTable extends React.Component<
  EditorModalTableProps,
  EditorModalTableState
> {
  private controlRef: any;

  public refs: Record<
    string,
    any | HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >;

  constructor(props) {
    super(props);

    // @ts-ignore
    this.controlRef = React.createRef();
  }

  state = {
    validateStateSuccess: {},
    validateState: {},
    values: {},
    hiddenColumns: [],
    isFormLocked: false,
  };

  componentDidMount() {
    const values = this.getFieldValue();
    const { columns, schema } = this.props;

    const hidden = columns.filter((col) => {
      return !schema.columns.some((elem) => {
        return col.field === elem.field;
      });
    });

    this.setState({
      values: {
        ...values,
        ...(this.props.schema?.extra?.copy ? this.props.copiedValues : {}),
      },
      validateStateSuccess: {
        ...(this.props?.value?.okpdDescription ||
        this.props.copiedValues?.okpdDescription
          ? {
              projectProductOKPD:
                this.props?.value?.okpdDescription ??
                this.props.copiedValues?.okpdDescription,
            }
          : {}),
        ...(this.props?.value?.tnvedDescription
          ? {
              projectProductTNVED: this.props?.value?.tnvedDescription,
            }
          : {}),
      },
      hiddenColumns: [...hidden],
    });

    getEntries(this.refs).forEach(([key, ref]) => {
      if (
        ref?.classList?.contains('checkset') &&
        !ref.getAttribute('data-value') &&
        this.props?.copiedValues?.[key]
      ) {
        ref.setAttribute(
          'data-value',
          JSON.stringify(this.props.copiedValues[key]),
        );
      }
    });
  }

  componentDidUpdate(
    prevProps: Readonly<EditorModalTableProps>,
    prevState: Readonly<EditorModalTableState>,
    snapshot?: any,
  ): void {
    if (prevState.values !== this.state.values) {
      /**
       * Из-за большой задержки между изменением в инпуте
       * и изменением в стейте, блокирую форму на изменение в форме
       * разблокирую её здесь, когда изменения синхронизировались
       */
      if (this.state.isFormLocked) this.unlockForm();

      const visibleColumns = this.getVisibleColumns(this.props.columns);

      const hidden = this.props.columns.filter((col) => {
        return !visibleColumns.some((elem) => {
          return col.field === elem.field;
        });
      });

      this.setState({
        hiddenColumns: [...hidden],
      });
    }
  }

  /**
   * блокировка сохранения на изменение формы
   * с обработкой кейсов, где блокировка не снимется и блокировать не нужно
   */
  handleLockForm({
    inputName,
    inputValue,
  }: {
    inputName: string | undefined;
    inputValue: any;
  }) {
    const schemaForField = this.props.schema.columns.find(
      ({ field }) => field === inputName,
    );

    if (!schemaForField) return;

    /**
     * для кейса, в котором у схемы поля есть maxLength
     * и ивент отработал, но у поля превышен maxLength и значение не изменилось,
     * не нужно лочить, т.к. изменения после батчинга не дойдут до обработчика анлока
     */
    if (
      !!schemaForField.maxLength &&
      typeof inputValue === 'string' &&
      inputValue.length > schemaForField.maxLength
    ) {
      /**
       * если длина текущего значения уже равна максимальному,
       * на спам ctrl+v можно сломать и заблочить форму
       * на этот кейс разлочиваем вручную е
       */
      if (
        this.state.values[inputName]?.length === schemaForField.maxLength &&
        this.state.isFormLocked
      )
        this.unlockForm();
      return;
    }

    this.lockForm();
  }

  lockForm() {
    this.setState({ isFormLocked: true });
  }

  unlockForm() {
    this.setState({ isFormLocked: false });
  }

  // TODO: unknown return result !!!
  /**
   * Метод, возвращающий значения всех полей
   * Берёт их не через стейт, а через this.refs, выбирая нужные через column
   */
  getFieldValue = () => {
    const newRow: Record<string, unknown> = {};
    const {
      schema: { generateUUID },
    } = this.props;

    this.props.columns.forEach((column: TableColum) => {
      // Исключаем из values сервисный тип, чтобы не ломал отправку объекта
      if (column.type?.toUpperCase() === 'I18N_COMPLEX') {
        return;
      }
      if (generateUUID && !isEmpty(this.props.value?.[generateUUID])) {
        newRow[generateUUID] = this.props.value[generateUUID];
      }
      if (!this.isColumnSpecial(column) && this.refs?.[column?.field]) {
        let value = null;
        if (column?.type?.toUpperCase() === 'RADIOBUTTON') {
          value = this.refs[column.field]?.dataset?.value;
        } else {
          value = this.refs[column.field]?.dataset?.value
            ? JSON.parse(this.refs[column.field].dataset.value)
            : undefined;
        }

        try {
          newRow[column.field] =
            this.refs[column.field]?.value ||
            value ||
            // @ts-ignore
            this.refs[column.field]?.state?.formattedNumber;
          /**
           * TODO: для инпута типа "Calendar" не отрабатывает корректно onMount
           *  если предыдущие 3 значения не бросают ошибку,
           *  нужно проверять внутри this.refs?.[column?.field].state
           *  !Возможно, не отрабатывает ещё для каких-то инпутов
           *  Для корректного выбора след.значения нужно знать все варианты,
           *  которые могут привести к этому чеку
           *
           * Для Calendar пока пофиксил костылём через проп "shouldUpdateValue"
           * */
        } catch (error) {
          // @ts-ignore
          newRow[column.field] =
            value || this.refs?.[column?.field]?.props?.value;
        }
      } else if (
        column.type?.toUpperCase() === 'DYNAMICCONTROL' &&
        this.refs[column?.field] === undefined &&
        this.props?.value?.[column.field]
      ) {
        newRow[column.field] = this.props.value[column.field];
      } else {
        const key =
          this.props?.schema?.id === 'contactsStaff' ? 'new_contact_' : 'key_';

        newRow[column.field] = column.isKey
          ? `${key}${getRandomKey()}`
          : this.applyColumnLogic(this.props.columns, column);
      }
    }, this);
    if (this.props.mode === 'title') {
      newRow.spec_col_names = true;
    }

    return newRow;
  };

  isColumnSpecial = (column: TableColum): boolean => {
    if (column.isKey) {
      return true;
    }

    if (column?.isHiddenInModal) {
      return true;
    }

    if (column.type && column.type.toUpperCase() === 'I18N_COMPLEX') {
      return true;
    }
    // @ts-ignore
    return column?.extra?.logic === 'row_sum';
  };

  handleRowWithTnvedOkpd = (rows) => {
    return {
      ...rows,
      // @ts-ignore
      okpdDescription: this.state.validateStateSuccess.projectProductOKPD
        ? // @ts-ignore
          this.state.validateStateSuccess.projectProductOKPD
        : rows.okpdDescription,
      // @ts-ignore
      tnvedDescription: this.state.validateStateSuccess.projectProductTNVED
        ? // @ts-ignore
          this.state.validateStateSuccess.projectProductTNVED
        : rows.tnvedDescription,
    };
  };

  async asyncValidateField({
    value,
    type,
    field,
    validateState,
  }: {
    value: string;
    type: 'OKPD' | 'TNVED';
    field: string;
    validateState: {};
  }): Promise<void> {
    let validate: (
      id: string,
      handlers: {
        [key: string]: (...params: any[]) => void;
      },
    ) => void;

    switch (type) {
      case 'OKPD':
        validate = this.props.api.validateOkpd;
        break;
      case 'TNVED':
        validate = this.props.api.validateTnved;
        break;
      default:
        break;
    }

    return new Promise<void>((resolve, reject) => {
      validate(value, {
        success: ({ correct, message }) => {
          if (!correct) validateState[field] = message;
          resolve();
        },
        error: (_status, message) => {
          console.log(message);
          reject();
        },
      });
    });
  }

  /**
   * метод ближе к onSubmit
   * метод, срабатывающий на submit кнопку
   * валидирует все значения формы, и, если всё ок,
   * запускает actual onSubmit логику
   */
  onClick = async () => {
    const {
      schema: { generateUUID },
    } = this.props;
    const rows = this.getFieldValue();
    const validateState = {};
    let errors: Dictionary<IControlError>;

    const asyncValidationPromises: Promise<void>[] = [];

    this.props?.columns?.forEach((column: TableColum, index) => {
      if (
        this.state.hiddenColumns?.find((elem) => elem.field === column.field)
      ) {
        return;
      }

      if (!this.isColumnSpecial(column)) {
        const schema = (column) => ({
          ...column,
          id: column.field,
          type: column?.extra?.valueType || column.type,
          contentType: column?.extra?.valueType || column.type,
        });

        const transformedValues = (data) => [
          ...Object.keys(data).map((value) => ({
            controlid: value,
            value: data[value],
          })),
        ];

        const columnType: string = schema(column)?.type?.toUpperCase();

        /**
         * Для полей требуется валидация на стороне сервера
         * Создаём промис для каждого, чтобы приостановить валидацию
         * и валидировать после ответа
         */
        if (columnType === 'OKPD' || columnType === 'TNVED') {
          const value = transformedValues(rows).find(
            ({ controlid }) => controlid === column.field,
          )?.value;

          const promise = this.asyncValidateField({
            value,
            field: column.field,
            type: columnType,
            validateState,
          });

          asyncValidationPromises.push(promise);
          return;
        }

        const validate = Array.from(
          validateControl(
            schema(column),
            transformedValues(rows),
            null,
            true,
            errors,
          ),
        );

        if (
          validate.length > 0 &&
          validate[0].controlId === column.field &&
          typeof validate[0].error !== 'undefined'
        ) {
          validateState[column.field] = validate[0].error.message;
        }

        const checkDuplicate = ({
          values,
          field,
          comparisonValue = field,
          message = 'Неверно заполнено поле',
        }) => {
          if (Array.isArray(values)) {
            const duplicateExists = values.some(
              (value) =>
                value[comparisonValue] === rows[column.field] &&
                this.props.value?.key !== value.key,
            );

            if (duplicateExists) {
              validateState[column.field] = message;
            }
          }
        };

        if (column.extra?.checkDuplicateInCurrentGrid) {
          checkDuplicate({
            values: this.props.schema?.value,
            field: column.field,
            message: column.extra.checkDuplicateInCurrentGrid?.message,
          });
        }

        if (column.extra?.checkDuplicateInAnotherGrid) {
          const { grid, field, message } =
            column.extra.checkDuplicateInAnotherGrid;
          checkDuplicate({
            values: this.props.dispatcher.pullValue(grid),
            field: column.field,
            comparisonValue: field,
            message,
          });
        }
      } else if (column.isKey && this.props?.value)
        rows[column.field] = this.props.value[column.field];
    }, this);

    /**
     * Ждём резолва сервер-сайд валидации ошибок
     */
    await Promise.all(asyncValidationPromises);

    if (validateState) {
      const firsErrorElementKey = Object.keys(validateState)?.[0];
      // eslint-disable-next-line no-unused-expressions
      this.refs[firsErrorElementKey]?.scrollIntoView &&
        this.refs[firsErrorElementKey].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
    }

    this.setState({ validateState });

    if (isEmpty(validateState)) {
      if (generateUUID && isEmpty(this.props.value?.[generateUUID])) {
        rows[generateUUID] = Guid.newGuid();
      }

      this.props.onSave(this.handleRowWithTnvedOkpd(rows), this.props.mode);
      this.props.onModalClose();
    } else {
      for (let k = 0; k < this.props.columns.length; k++) {
        if (typeof validateState[this.props.columns[k].field] !== 'undefined') {
          if (this.refs[this.props.columns[k].field]) {
            // eslint-disable-next-line no-unused-expressions
            typeof this.refs[this.props.columns[k].field] === 'function' &&
              this.refs[this.props.columns[k].field].scrollIntoView({
                block: 'center',
              });
            k = this.props.columns.length;
          }
        }
      }
    }
  };

  /**
   * Функция нужна для того чтобы записывать значения для SelectBlock
   *  Другие функции как например ниже не используем, потому что там работа с текстом
   *  (валидация и прочее, что может вызывать проблемы с массивами)
   *  TODO Потом надо переписать взаимодействие, так чтоб метод обновления состояния был один, а всяческие обработки спрятать внутри каждого компонента/поля
   */

  setAdapterValue = (column) => (event) => {
    const currentField = this.refs[column.field];
    let fieldValue =
      typeof currentField !== 'undefined' &&
      (currentField.value ? currentField.value : currentField?.state?.value);

    this.setState(({ values }) => {
      const _values = { ...values };
      _values[column.field] = fieldValue;
      // TODO Здесь сделать валидацию если она потребуется (возможно валидация внутри компонента может помочь)
      // по типу YUP/ или схемоподобное что бы не плодить функции
      return {
        values: _values,
      };
    });
  };

  setValueState = (event, column) => {
    const currentField = this.refs[column.field];

    let fieldValue = typeof currentField !== 'undefined' && currentField.value;

    if (column.extra?.valueType?.toUpperCase() === 'DATEMONTHANDYEAR') {
      fieldValue = dateFormat(fieldValue);
    }

    this.setState(({ values }) => {
      const _values = { ...values };
      _values[column.field] = trimString(column.maxLength, fieldValue);

      if (column.type.toUpperCase() === 'FLOAT') {
        if (
          this.props.columns.find(
            (column) =>
              Array.isArray(column?.defaultValue) &&
              column.defaultValue.includes('sumRow'),
          )
        ) {
          const columnWhereNeedSum = this.props.columns.find((column) =>
            column?.defaultValue?.includes('sumRow'),
          );
          let sum = 0;

          for (const column of this.props.columns) {
            if (
              column?.type?.toUpperCase() === 'FLOAT' &&
              column.field !== columnWhereNeedSum.field
            ) {
              const value = values?.[column.field] ?? 0;
              sum += +value;
            }
          }

          _values[columnWhereNeedSum.field] = `${sum}`;
        }
      }
      return {
        values: _values,
      };
    });
  };

  setCheckState = (event, idx, column, option) => {
    if (this.props.disabled) {
      return;
    }
    const currentField = this.refs[column.field];

    if (option) {
      const newVal = option.name;
      const fieldValue =
        currentField && currentField?.dataset?.value
          ? JSON.parse(currentField.dataset.value)
          : [];

      if (fieldValue.indexOf(newVal) > -1) {
        fieldValue.splice(fieldValue.indexOf(newVal), 1);
        if (fieldValue.length > 0) {
          currentField.dataset.value = JSON.stringify([...fieldValue]);
        } else {
          currentField.dataset.value = JSON.stringify([]);
        }
        event.currentTarget.checked = false;
      } else {
        currentField.dataset.value = JSON.stringify([...fieldValue, newVal]);
        event.currentTarget.checked = 'checked';
      }

      this.setState(({ values }) => ({
        values: {
          ...values,
          [column.field]: JSON.parse(currentField.dataset.value),
        },
      }));
    } else {
      const fieldValue = currentField.dataset.value === 'true' || false;
      currentField.dataset.value = (!fieldValue).toString();
      event.currentTarget.checked = !fieldValue;

      this.setState(({ values }) => ({
        values: {
          ...values,
          [column.field]: !fieldValue,
        },
      }));
    }
  };

  applyColumnLogic = (
    columns: Array<TableColum>,
    column: TableColum,
  ): string => {
    // @ts-ignore
    if (column?.extra?.logic === 'row_sum') {
      let num_res = 0;

      for (const col of columns) {
        if (!this.isColumnSpecial(col)) {
          const field = this.refs[col.field];

          if (field && field.value && col.type === 'float') {
            const n = parseFloatCommaDot(field.value);
            if (!Number.isNaN(n)) {
              num_res += n;
            }
          }
        }
      }
      return numberToFixedString(num_res);
    }

    if (column?.defaultValue) {
      if (
        this.props.value?.[column?.field] === undefined ||
        this.props.value?.[column?.field] === column.defaultValue
      ) {
        return column.defaultValue;
      }
    }

    return '';
  };

  setRadioState = (event, idx, column, option) => {
    if (this.props.disabled) {
      return;
    }
    const currentField = this.refs[column.field];

    if (option) {
      currentField.dataset.value = String(option.name);
      event.currentTarget.checked = 'checked';
    }

    this.setState(({ values }) => ({
      values: {
        ...values,
        [column.field]: String(option.name),
      },
    }));
  };

  checkIfFormat = (format: 'number' | 'float') => {
    const _formats = {
      number: /[0-9]+/,
      float: /[0-9\.\,]+/,
    };

    return (event: React.KeyboardEvent<HTMLInputElement>): void => {
      const charCode = event?.which ?? event?.keyCode;
      const charStr = String.fromCharCode(charCode);

      if (!charStr.match(_formats[format])) {
        event.preventDefault();
      }
    };
  };

  checkIfPhone = (column: TableColum): void => {
    const { values } = this.state;
    const currentValue = this.refs[column.field].value;

    values[column.field] = phoneFix(currentValue);

    this.setState({ values });
    this.refs[column.field].value = values[column.field];
  };

  // TODO: DRY
  setDateState = (columnField: string) => (value: string | DateRange) => {
    this.refs[columnField].value = value;
    this.setState((prevState) => ({
      values: {
        ...prevState.values,
        [columnField]: value,
      },
    }));
  };

  // TODO: идет дубль - в дальнейшем разрулить ситуацию
  setPhoneState =
    (columnField: string) => (value, object, event, valuePhone) => {
      this.setState((prevState) => ({
        values: {
          ...prevState.values,
          [columnField]: formatterPhone(valuePhone),
        },
      }));
    };

  // ???
  setOKPDState = (event, columnField: string) => {
    const { value } = event.target;

    if (value !== this.state.values[columnField]) {
      this.setState(({ values, validateState, validateStateSuccess }) => {
        const shallowCopy = { ...validateState };
        delete shallowCopy?.[columnField];
        const shallowCopySuccess = { ...validateStateSuccess };
        delete shallowCopySuccess?.[columnField];
        return {
          values: {
            ...values,
            [columnField]: value,
            okpdDescription: '',
          },
          validateStateSuccess: shallowCopySuccess,
          validateState: shallowCopy,
        };
      });

      if (getDigits(value).length === 9) {
        this.props.api.validateOkpd(value, {
          success: (response) => {
            if (response) {
              this.setState(
                ({ values, validateStateSuccess, validateState }) => {
                  const { correct, message } = response;
                  return {
                    values: {
                      ...values,
                      okpdDescription: message,
                    },
                    validateStateSuccess: correct
                      ? {
                          ...validateStateSuccess,
                          [columnField]: message,
                        }
                      : validateStateSuccess,
                    validateState: !correct
                      ? {
                          ...validateState,
                          [columnField]: message,
                        }
                      : validateState,
                  };
                },
              );
            }
          },
          error: () => {
            this.setState(({ validateState }) => ({
              validateState: {
                ...validateState,
                [columnField]: 'Ошибка при загрузке',
              },
            }));
          },
        });
      }
    }
  };

  // ???
  setTNVEDState = (event, columnField: string) => {
    const { value } = event.target;

    if (value !== this.state.values[columnField]) {
      this.setState(({ values, validateState, validateStateSuccess }) => {
        const shallowCopy = { ...validateState };
        delete shallowCopy?.[columnField];
        const shallowCopySuccess = { ...validateStateSuccess };
        delete shallowCopySuccess?.[columnField];
        return {
          values: {
            ...values,
            [columnField]: value,
            tnvedDescription: '',
          },
          validateStateSuccess: shallowCopySuccess,
          validateState: shallowCopy,
        };
      });

      if (getDigits(value).length === 10) {
        this.props.api.validateTnved(value, {
          success: (response) => {
            if (response) {
              this.setState(
                ({ values, validateStateSuccess, validateState }) => {
                  const { correct, message } = response;
                  return {
                    values: {
                      ...values,
                      tnvedDescription: message,
                    },
                    validateStateSuccess: correct
                      ? {
                          ...validateStateSuccess,
                          [columnField]: message,
                        }
                      : validateStateSuccess,
                    validateState: !correct
                      ? {
                          ...validateState,
                          [columnField]: message,
                        }
                      : validateState,
                  };
                },
              );
            }
          },
          error: () => {
            this.setState(({ validateState }) => ({
              validateState: {
                ...validateState,
                [columnField]: 'Ошибка при загрузке',
              },
            }));
          },
        });
      }
    }
  };

  // Просилось в грантах запретить вставку у числовых значений
  stopDefaultEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  createControl = (_column: TableColum, _isDynamic?: boolean) => {
    // eslint-disable-next-line prefer-const
    let { value, disabled, rowCount } = this.props;

    const column = { ..._column };

    // Предзаполнение заполненными данными Grid
    const defaultValue =
      this.props.schema?.defaultValue?.[rowCount]?.[column.field] ?? '';
    if (this.props.schema?.defaultValue) {
      disabled =
        !!this.props.schema?.defaultValue?.[rowCount]?.[column.field] ?? false;
    }
    if (this.props.schema?.ifFieldIsFilledThenBlockRow) {
      disabled =
        !!this.props.value?.[this.props.schema.ifFieldIsFilledThenBlockRow] ??
        disabled;
    }

    // создание url для компонентов с data.url
    if (!!column.data?.url) {
      const urlBuilt = urlBuilder({
        urlData: column.data,
        fieldValues: this.getFieldsValues(),
        api: this.props.api,
      });

      column.data = { ...column.data, url: urlBuilt };
    }

    if (column.editableOnChange === false) {
      if (value && value?.[column.field]) {
        if (
          typeof value[column.field] === 'string' &&
          value[column.field].length > 0
        )
          column.editable = false;
        else column.editable = false;
      } else {
        column.editable = true;
      }
    }

    if (column.extra?.i18n) {
      const i18nControl = this.props.columns.find((item) => {
        let isAvailable = true;
        if (item.rule) {
          // @ts-ignore
          item.rule.forEach((itemRule) => {
            if (itemRule.option === 'Equal') {
              isAvailable =
                this.props.dispatcher.pullValue(itemRule.controlTargetId) ===
                itemRule.value;
            }
          });
        }

        return (
          column.extra.i18n.includes(item.field) &&
          item.type === 'i18n_complex' &&
          isAvailable
        );
      });

      if (i18nControl) {
        column.extra.i18nControls = i18nControl;
      }
    }

    if (column.name === '№') {
      return (
        <Description
          field={column.field}
          extra={column?.extra}
          description={column?.description}
          notification={column?.notification}
        >
          <span ref={column.field}>
            {(value
              ? value?.[column?.field]
              : this.props.rowCount + 1
            ).toString()}
          </span>
        </Description>
      );
    }
    if (column.type === 'Label') {
      return (
        <Description
          field={column.field}
          extra={column?.extra}
          description={column?.description}
          notification={column?.notification}
        >
          <label className="control-label" />
        </Description>
      );
    }
    if (column.extra) {
      if (column.extra.lookup) {
        const lookup_columns = [];
        const lookup_components = [];

        if (Array.isArray(column.extra.lookup)) {
          for (const item of column.extra.lookup) {
            if (item.column && item.component) {
              lookup_columns.push(item.column);
              lookup_components.push(item.component);
            }
          }
        } else {
          lookup_columns.push(column.extra.lookup.column);
          lookup_components.push(column.extra.lookup.component);
        }

        let lookup_values_list = [];
        for (let i = 0; i < lookup_columns.length; i++) {
          const lookup_column = lookup_columns[i];
          const lookup_component = lookup_components[i];

          if (lookup_column && lookup_component) {
            const lookup_values = this.props.dispatcher.pullTableRows(
              lookup_component,
              lookup_column,
            );
            lookup_values_list = lookup_values_list.concat(lookup_values);
          }
        }
        return (
          <Description
            field={column.field}
            extra={column?.extra}
            description={column?.description}
            notification={column?.notification}
          >
            <select
              ref={column.field}
              className="form-control"
              defaultValue={value && value[column.field]}
              disabled={
                (column?.editable && column.editable === false) || !!disabled
              }
            >
              {lookup_values_list.map((str) => {
                if (!str) {
                  return <option key={0} value={null} aria-label="пусто" />;
                }
                return (
                  <option key={str} value={str}>
                    {str}
                  </option>
                );
              })}
            </select>
          </Description>
        );
      }
    }
    if (column.type !== null) {
      switch (column.type.toUpperCase()) {
        case 'SELECT':
          // @ts-ignore
          if (column?.extra?.init) {
            // @ts-ignore
            switch (column?.extra.init) {
              case 'getProjects':
                if (this.props.opts?.projects) {
                  // @ts-ignore
                  column?.options = this.props.opts.projects;
                }
                break;
              default:
                break;
            }
          }

          return (
            <Description
              field={column.field}
              extra={column?.extra}
              description={column?.description}
              notification={column?.notification}
            >
              <select
                ref={column.field}
                // ref={isDynamic ? (node => this.refs[column.field] = node) : column.field}
                className="form-control"
                defaultValue={value && value[column.field]}
                disabled={column?.editable === false || !!disabled}
              >
                {column?.options &&
                  column?.options?.map((str) => {
                    if (!str) {
                      return <option key={0} value={null} aria-label="пусто" />;
                    }

                    if (str.crm_id) {
                      return (
                        <option
                          key={str.name}
                          value={
                            column?.valueIs === 'key' ? str?.key : str.name
                          }
                        >
                          {str.name}
                        </option>
                      );
                    }
                    return (
                      <option
                        key={str.value}
                        value={
                          column?.valueIs === 'key'
                            ? str?.key
                            : str?.label?.ru || str.value.ru
                        }
                      >
                        {(str?.label?.ru ? str.label.ru : str.value.ru) || str}
                      </option>
                    );
                  })}
              </select>
            </Description>
          );
        case 'NUMBER':
          return (
            <>
              <Description
                field={column.field}
                extra={column?.extra}
                description={column?.description}
                notification={column?.notification}
              >
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-control editor edit-text"
                  onKeyPress={this.checkIfFormat('number')}
                  onPaste={this.stopDefaultEvents}
                  ref={column.field}
                  // ref={isDynamic ? (node => this.refs[column.field] = node) : column.field}
                  disabled={column?.editable === false || !!disabled}
                  defaultValue={
                    !this.props?.value
                      ? defaultValue
                      : this.props.value?.[column?.field] || ''
                  }
                  value={this.state.values[column?.field]}
                  onChange={(e) => {
                    this.setValueState(e, column);
                  }}
                />
              </Description>
              {column.maxLength &&
                (column.maxLength > 0 || column.minLength > 0) && (
                  <div className="input-counter">
                    {createCounterMessage(
                      this.state.values[column.field],
                      column.maxLength,
                      column.minLength,
                    )}
                  </div>
                )}
            </>
          );
        case 'FLOAT': {
          return (
            <Description
              field={column.field}
              extra={column?.extra}
              description={column?.description}
              notification={column?.notification}
            >
              <input
                type="text"
                className="form-control editor edit-text"
                onKeyPress={this.checkIfFormat('float')}
                onPaste={this.stopDefaultEvents}
                onChange={(event) => this.setValueState(event, column)}
                ref={column.field}
                // ref={isDynamic ? (node => this.refs[column.field] = node) : column.field}
                disabled={column?.editable === false || !!disabled}
                defaultValue={
                  !this.props?.value
                    ? defaultValue
                    : this.props.value?.[column?.field] || ''
                }
                value={this.state.values[column?.field]}
              />
            </Description>
          );
        }
        case 'FILE':
          return (
            <Description
              field={column.field}
              extra={column?.extra}
              description={column?.description}
              notification={column?.notification}
            >
              <GridMultipleFiles
                field={column}
                defaultValue={this.props?.value?.[column?.field] || null}
                api={this.props.api}
                dispatcher={this.props.dispatcher}
                onSave={this.props.onSave}
                ref={column.field}
                row={value}
                file_formats={
                  column.format ||
                  (column.extra && column.extra.file_formats
                    ? column.extra.file_formats
                    : [])
                }
              />
            </Description>
          );
        case 'TEXTAREA': {
          return (
            <GridComponentAdapter
              Component={TextBox}
              schema={column}
              disabled={column?.editable === false || !!disabled}
              api={this.props.api}
              dispatcher={this.props.dispatcher}
              value={
                this.state.values?.[column?.field] ||
                this.props.value?.[column?.field]
              }
              onChange={this.setAdapterValue(column)}
              opts={this.props.opts}
              ref={column.field}
              setRef={(value: any) => {
                this.refs[column.field] = {
                  value,
                };
              }}
              defaultValue={
                !this.props?.value
                  ? defaultValue
                  : this.props.value?.[column?.field] || ''
              }
            />
          );
        }
        case 'CONSTLINK':
          return (
            <Description
              field={column.field}
              extra={column?.extra}
              description={column?.description}
              notification={column?.notification}
            >
              {value?.[column?.field] ? (
                <a
                  ref={column.field}
                  data-value={JSON.stringify(this.props.value[column.field])}
                  href={value ? value?.[column?.field].link_url : ''}
                  className="form-link"
                >
                  {value ? value?.[column?.field].link_title : ''}
                </a>
              ) : (
                ''
              )}
            </Description>
          );
        case 'CHECKBOXSET':
          const currValue = this.state?.values?.[column?.field] || [];

          return (
            <Description
              field={column.field}
              extra={column?.extra}
              description={column?.description}
              notification={column?.notification}
            >
              <div
                className="input-group-cr checkset"
                ref={column.field}
                // @ts-ignore
                // ref={isDynamic ? (node => this.refs[column.field] = node) : column.field}
                data-value={
                  this.props?.value
                    ? JSON.stringify(this.props.value[column.field])
                    : null
                }
              >
                <div
                  className={`btn-group ${
                    column?.extra?.columns ? 'custom-row' : ''
                  }`}
                >
                  {column?.options?.map((option, idx) => (
                    // TODO: Отрефачить это поведение - повесить на чекбокс onChange() и убрать у лейбла onClick()
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                    <label
                      key={idx}
                      htmlFor={option.name}
                      className="checkset-inline"
                      onClick={
                        option.disabled || disabled
                          ? null
                          : (event) =>
                              this.setCheckState(event, idx, column, option)
                      }
                      style={{
                        width: `${
                          column?.extra?.columns
                            ? 100 / column?.extra?.columns
                            : 100
                        }%`,
                      }}
                    >
                      <input
                        type="checkbox"
                        className="checkset__checkbox"
                        disabled={option.disabled || disabled}
                        name={option.name}
                        data-value={option.name}
                        checked={
                          currValue && currValue?.indexOf(option.name) > -1
                        }
                        readOnly
                      />
                      <span
                        dangerouslySetInnerHTML={{ __html: option.label }}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </Description>
          );
        case 'RADIOBUTTON':
          const val = this.state?.values?.[column?.field];

          return (
            <Description
              field={column.field}
              extra={column?.extra}
              description={column?.description}
              notification={column?.notification}
            >
              <div
                className="input-group-cr"
                ref={column.field}
                // @ts-ignore
                // ref={isDynamic ? (node => this.refs[column.field] = node) : column.field}
                data-value={this.props?.value?.[column?.field] || null}
              >
                <div className="btn-group">
                  {column?.options &&
                    column?.options?.map((option, idx) => (
                      // TODO: Отрефачить это поведение - повесить на чекбокс onChange() и убрать у лейбла onClick()
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                      <label
                        key={idx}
                        className="checkset-inline"
                        onClick={
                          option.disabled || disabled
                            ? null
                            : (event) =>
                                this.setRadioState(event, idx, column, option)
                        }
                      >
                        <input
                          type="radio"
                          disabled={option.disabled || disabled}
                          name={column.field}
                          data-value={String(option.name)}
                          checked={val && String(val) === String(option.name)}
                          readOnly
                        />
                        <span
                          dangerouslySetInnerHTML={{ __html: option.label }}
                        />
                      </label>
                    ))}
                </div>
              </div>
            </Description>
          );
        case 'CHECKBOX':
          const curr = this.state?.values?.[column?.field] || false;

          return (
            <Description
              field={column.field}
              extra={column?.extra}
              description={column?.description}
              notification={column?.notification}
            >
              <div
                className="input-group-cr checkset"
                ref={column.field}
                data-value={this.props?.value?.[column?.field] || null}
              >
                <div className="btn-group">
                  {column?.name && (
                    // TODO: Отрефачить это поведение - повесить на чекбокс onChange() и убрать у лейбла onClick()
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                    <label
                      htmlFor={column.field}
                      className="checkset-inline"
                      onClick={
                        column.disabled || disabled
                          ? null
                          : (event) =>
                              this.setCheckState(event, null, column, null)
                      }
                    >
                      <input
                        type="checkbox"
                        className="checkset__checkbox"
                        disabled={column.disabled || disabled}
                        name={column.field}
                        checked={curr}
                        readOnly
                      />
                      {column?.label && (
                        <span
                          dangerouslySetInnerHTML={{ __html: column.label }}
                        />
                      )}
                    </label>
                  )}
                </div>
              </div>
            </Description>
          );
        case 'CALENDAR':
          return (
            // TODO: DRY (706 && 726) !!! REBUILD
            <Description
              field={column.field}
              extra={column?.extra}
              description={column?.description}
              notification={column?.notification}
            >
              <GridCalendar
                ref={column.field}
                isRanged={column?.isRanged}
                multipleProps={column?.extra?.multiple}
                isMultiple={false}
                disabled={column?.editable === false || !!disabled}
                value={
                  this.state.values?.[column?.field] ||
                  this.props.value?.[column?.field]
                }
                shouldUpdateValue={
                  !this.state.values?.[column?.field] &&
                  !!this.props.value?.[column?.field]
                }
                onChangeValue={this.setDateState(column?.field)}
              />
            </Description>
          );
        case 'RANGEDMULTICALENDAR':
          return (
            <Description
              field={column.field}
              extra={column?.extra}
              description={column?.description}
              notification={column?.notification}
            >
              <GridCalendar
                ref={column.field}
                isRanged
                multipleProps={column?.extra?.multiple}
                isMultiple
                disabled={column?.editable === false || !!disabled}
                value={
                  this.state.values?.[column?.field] ||
                  this.props.value?.[column?.field]
                }
                onChangeValue={this.setDateState(column?.field)}
                shouldUpdateValue={
                  !this.state.values?.[column?.field] &&
                  !!this.props.value?.[column?.field]
                }
              />
            </Description>
          );
        case 'TELEPHONE':
          return (
            <>
              <Description
                field={column.field}
                extra={column?.extra}
                description={column?.description}
                notification={column?.notification}
              >
                <PhoneInput
                  // @ts-ignore
                  ref={column.field}
                  autoFormat
                  defaultCountry="ru"
                  country="ru"
                  onlyCountries={COUNTRIES_CODE}
                  value={
                    this.state.values[column?.field] ||
                    this.props.value?.[column?.field]
                  }
                  disabled={column?.editable === false || !!disabled}
                  onChange={this.setPhoneState(column.field)}
                />
              </Description>
              {column.maxLength &&
                (column.maxLength > 0 || column.minLength > 0) && (
                  <div className="input-counter">
                    {createCounterMessage(
                      this.state.values?.[column?.field],
                      column.maxLength,
                      column.minLength,
                    )}
                  </div>
                )}
            </>
          );
        case 'DYNAMICCONTROL': {
          return (
            // TODO: Add URL
            <DynamicControl>
              {({ loading, error, data }) => {
                if (loading)
                  return (
                    <div>
                      <Dots />
                    </div>
                  );
                if (error) return <div>{error}</div>;

                return this.createControl(data, true);
              }}
            </DynamicControl>
          );
        }
        case 'DROPBOX': {
          return (
            <GridComponentAdapter
              Component={DropBox}
              schema={column}
              disabled={disabled}
              api={this.props.api}
              dispatcher={this.props.dispatcher}
              value={
                this.state.values?.[column?.field] ||
                this.props.value?.[column?.field]
              }
              onChange={this.setAdapterValue(column)}
              opts={this.props.opts}
              ref={column.field}
              setRef={(value: any) => {
                this.refs[column.field] = {
                  value,
                };
              }}
            />
          );
        }
        case '_FILE': {
          return (
            <GridComponentAdapter
              Component={FilesControl}
              schema={column}
              disabled={disabled}
              api={this.props.api}
              dispatcher={this.props.dispatcher}
              value={
                this.state.values?.[column?.field] ||
                this.props.value?.[column?.field]
              }
              onChange={this.setAdapterValue(column)}
              opts={this.props.opts}
              ref={column.field}
              setRef={(value: any) => {
                this.refs[column.field] = {
                  value,
                };
              }}
            />
          );
        }
        case 'SELECTBLOCK': {
          //Для понимания что это такое.
          // Этот адаптер для того чтоб протаскивать обычные поля для Grid
          // ref = нужен для инициализации this.refs
          // setRef = устанавливает новое значение для this.refs (когда внутри компонента поменялось значение)
          // console.log('column', column);
          // console.log('value', this.state.values?.[column?.field] || this.props.value?.[column?.field]);
          return (
            <GridComponentAdapter
              Component={SelectBlock}
              schema={column}
              disabled={disabled}
              api={this.props.api}
              dispatcher={this.props.dispatcher}
              value={
                this.state.values?.[column?.field] ||
                this.props.value?.[column?.field]
              }
              onChange={this.setAdapterValue(column)}
              opts={this.props.opts}
              ref={column.field}
              setRef={(value: any) => {
                this.refs[column.field] = {
                  value,
                };
              }}
            />
          );
        }
        case 'WORKEXPERIENCELIST': {
          return (
            <GridComponentAdapter
              Component={WorkExperienceList}
              schema={column}
              disabled={disabled}
              api={this.props.api}
              dispatcher={this.props.dispatcher}
              value={
                this.props.value?.[column?.field] ||
                this.state.values?.[column?.field]
              }
              onChange={this.setAdapterValue(column)}
              opts={this.props.opts}
              ref={column.field}
              setRef={(value: any) => {
                this.refs[column.field] = {
                  value,
                };
              }}
            />
          );
        }
        case 'TEXTBOX': {
          if (column?.extra && column?.extra?.valueType === 'phone') {
            return (
              <>
                <Description
                  field={column.field}
                  extra={column?.extra}
                  description={column?.description}
                  notification={column?.notification}
                >
                  <input
                    type="text"
                    className="form-control editor edit-text"
                    onKeyUp={() => this.checkIfPhone(column)}
                    onChange={(event) => this.setValueState(event, column)}
                    ref={column.field}
                    // ref={isDynamic ? (node => this.refs[column.field] = node) : column.field}
                    disabled={column?.editable === false || !!disabled}
                    defaultValue={
                      !this.props?.value
                        ? defaultValue
                        : this.props.value?.[column?.field] || ''
                    }
                    value={this.state.values[column?.field]}
                  />
                </Description>
                {column.maxLength &&
                  (column.maxLength > 0 || column.minLength > 0) && (
                    <div className="input-counter">
                      {createCounterMessage(
                        this.state.values?.[column?.field],
                        column.maxLength,
                        column.minLength,
                      )}
                    </div>
                  )}
              </>
            );
          }
          if (column?.extra && column?.extra?.valueType === 'okpd') {
            const digits = getDigits(
              this.state.values?.[column?.field] ||
                this.props.value?.[column?.field],
            );
            return (
              <>
                <Description
                  field={column.field}
                  extra={column?.extra}
                  description={column?.description}
                  notification={column?.notification}
                >
                  <MaskInput
                    // @ts-ignore
                    className="form-control editor edit-text"
                    alwaysShowMask
                    maskChar="_"
                    mask="00.00.00.000"
                    placeholder="__.__.__.___"
                    onChange={(event) => this.setOKPDState(event, column.field)}
                    getReference={(ref) => {
                      this.refs[column.field] = ref;
                    }}
                    disabled={column?.editable === false || !!disabled}
                    value={
                      this.state.values?.[column?.field] ||
                      this.props.value?.[column?.field]
                    }
                  />
                </Description>
                {column.maxLength &&
                  (column.maxLength > 0 || column.minLength > 0) && (
                    <div className="input-counter">
                      {createCounterMessage(
                        digits,
                        column.maxLength,
                        column.minLength,
                      )}
                    </div>
                  )}
              </>
            );
          }
          if (column?.extra && column?.extra?.valueType === 'tnved') {
            const digits = getDigits(
              this.state.values?.[column?.field] ||
                this.props.value?.[column?.field],
            );
            return (
              <>
                <Description
                  field={column.field}
                  extra={column?.extra}
                  description={column?.description}
                  notification={column?.notification}
                >
                  <MaskInput
                    // @ts-ignore
                    className="form-control editor edit-text"
                    alwaysShowMask
                    maskChar="_"
                    mask="0000000000"
                    placeholder="__________"
                    onChange={(event) =>
                      this.setTNVEDState(event, column.field)
                    }
                    getReference={(ref) => {
                      this.refs[column.field] = ref;
                    }}
                    disabled={column?.editable === false || !!disabled}
                    value={
                      this.state.values?.[column?.field] ||
                      this.props.value?.[column?.field]
                    }
                  />
                </Description>
                {column.maxLength &&
                  (column.maxLength > 0 || column.minLength > 0) && (
                    <div className="input-counter">
                      {createCounterMessage(
                        digits,
                        column.maxLength,
                        column.minLength,
                      )}
                    </div>
                  )}
              </>
            );
          }
        }
        // TODO: нет ретюрна перед дефолтом и break
        // eslint-disable-next-line no-fallthrough
        default:
          // @ts-ignore
          if (column?.extra?.init) {
            // @ts-ignore
            switch (column?.extra.init) {
              case 'getResearchDirection':
                if (this.props.opts?.company) {
                  // TODO: !!!
                  // @ts-ignore
                  // eslint-disable-next-line react/no-direct-mutation-state
                  this.state.values[column?.field] =
                    this.props.opts.company.cluster_name;
                }
                break;
              default:
                break;
            }
          }

          return (
            <GridComponentAdapter
              Component={TextBox}
              schema={column}
              disabled={column?.editable === false || !!disabled}
              api={this.props.api}
              dispatcher={this.props.dispatcher}
              value={
                this.state.values?.[column?.field] ||
                this.props.value?.[column?.field]
              }
              onChange={this.setAdapterValue(column)}
              opts={this.props.opts}
              ref={column.field}
              setRef={(value: any) => {
                this.refs[column.field] = {
                  value,
                };
              }}
            />
          );
      }
    } else {
      return (
        <>
          <Description
            field={column.field}
            extra={column?.extra}
            description={column?.description}
            notification={column?.notification}
          >
            <input
              type="text"
              disabled={column?.editable === false || !!disabled}
              className="form-control editor edit-text"
              ref={column.field}
              // ref={isDynamic ? (node => this.refs[column.field] = node) : column.field}
              defaultValue={
                !this.props?.value
                  ? defaultValue
                  : this.props.value?.[column?.field] || ''
              }
            />
          </Description>
          {column.maxLength &&
            (column.maxLength > 0 || column.minLength > 0) && (
              <div className="input-counter">
                {createCounterMessage(
                  this.state.values[column.field],
                  column.maxLength,
                  column.minLength,
                )}
              </div>
            )}
        </>
      );
    }
  };

  isColumnRequired(column: TableColum): boolean {
    if (column?.extra?.renamed_required) {
      if (this.props?.titleRow?.[column.field]) {
        return this.props.titleRow[column.field] !== column.name;
      }
      return false;
    }
    return column.required;
  }

  getPropsValues() {
    const { columns } = this.props;

    return columns.reduce((acc, item) => {
      return [
        ...acc,
        { controlid: item.field, value: this.props.value?.[item.field] },
      ];
    }, []);
  }

  getFieldsValues() {
    const { columns } = this.props;

    const needValues = columns
      .filter((item) => item?.rule)
      .reduce((acc, item) => {
        const result = item.rule.reduce(
          (acc, itemRule) => [
            ...acc,
            {
              controlid: itemRule.controlTargetId,
              value: this.props.dispatcher.pullValue(itemRule.controlTargetId),
            },
          ],
          [],
        );
        return [...acc, ...result];
      }, []);

    const propsValue = this.getPropsValues();

    /**
     * Сначала идут данные с внутреннего окружения
     * !если передаются в пропсах
     */
    const valueToReturn = typeof propsValue === 'object' ? propsValue : [];

    /**
     * После фильтруются и добавляются при необходимости видимые
     */
    valueObjectToArray(this.state.values).forEach((stateValue) => {
      const existingEl = valueToReturn.find((el) => {
        /**
         * !выведенный тип stateValue некорректен. Имя ключа - controlid, не controlId
         * TODO: найти, откуда идёт тип stateValue и поправить
         */

        return (
          el?.controlid ===
          //@ts-ignore
          (stateValue as { controlid: string; value: any }).controlid
        );
      });

      /**
       * если нет элемента с сервера, либо value элемента с сервера равно значению из стейта, просто пушим новый элемент и выходим из функции
       */
      if (!existingEl || existingEl?.value === stateValue.value) {
        valueToReturn.push(stateValue);
        return;
      }

      /**
       * Если найден элемент с тем же ключом с сервера, но значение отличается,
       * значит, элемент видимый и значение в стейте изменилось.
       * Нужно переопределить значение
       */

      existingEl.value = stateValue.value;
    });

    /**
     * Затем идут внешние
     */

    if (typeof needValues === 'object') {
      valueToReturn.push(...needValues);
    }

    return valueToReturn;
  }

  getVisibleColumns = (columns) => {
    //get outer values
    const needValues = columns
      .filter((item) => item?.rule)
      .reduce((acc, item) => {
        const result = item.rule.reduce(
          (acc, itemRule) => [
            ...acc,
            {
              controlid: itemRule.controlTargetId,
              value: this.props.dispatcher.pullValue(itemRule.controlTargetId),
            },
          ],
          [],
        );
        return [...acc, ...result];
      }, []);

    const propsValue = this.getPropsValues();

    //round
    const ruleManager = new RuleManagers();

    /**
     * Сначала идут данные с внутреннего окружения
     * !если передаются в пропсах
     */
    let ruleManagerValuesToRound =
      typeof propsValue === 'object' ? propsValue : [];

    /**
     * После фильтруются и добавляются при необходимости видимые
     */
    valueObjectToArray(this.state.values).forEach((stateValue) => {
      const existingEl = ruleManagerValuesToRound.find((el) => {
        /**
         * !выведенный тип stateValue некорректен. Имя ключа - controlid, не controlId
         * TODO: найти, откуда идёт тип stateValue и поправить
         */

        return (
          el?.controlid ===
          //@ts-ignore
          (stateValue as { controlid: string; value: any }).controlid
        );
      });

      /**
       * если нет элемента с сервера, либо value элемента с сервера равно значению из стейта, просто пушим новый элемент и выходим из функции
       */
      if (!existingEl || existingEl?.value === stateValue.value) {
        ruleManagerValuesToRound.push(stateValue);
        return;
      }

      /**
       * Если найден элемент с тем же ключом с сервера, но значение отличается,
       * значит, элемент видимый и значение в стейте изменилось.
       * Нужно переопределить значение
       */

      existingEl.value = stateValue.value;
    });

    /**
     * Затем идут внешние
     */

    if (typeof needValues === 'object') {
      ruleManagerValuesToRound.push(...needValues);
    }

    return ruleManager.round(columns, ruleManagerValuesToRound);
  };

  // TODO STATUS-663 нужно дождаться способа реализации и использовать эту функцию для вызова предзаполненной формы для team member (раздел Команды)
  getPreFilledForm = (id: string) => {
    const {
      api: { moduleApi, setValueUrl },
    } = this.props;
    moduleApi &&
      moduleApi.getPreFilledFile(setValueUrl, 'member_application_doc', id, {
        success: (data) => {
          return data;
        },
        error: (error) => {
          console.log(error);
        },
      });
  };

  render() {
    const { columns, onModalClose } = this.props;

    const rowIsDisabled = this.props.schema.ifFieldIsFilledThenBlockRow
      ? this.props.value?.[this.props.schema.ifFieldIsFilledThenBlockRow]
      : false;

    return (
      <div
        onChange={(e) => {
          const input = e.target as HTMLInputElement;
          this.handleLockForm({
            inputName: input.name,
            inputValue: input.value,
          });
        }}
        className="modal-content react-bs-table-insert-modal"
      >
        <div className="modal-header ">
          <button type="button" className="close" onClick={onModalClose}>
            <span aria-hidden="true">×</span>
          </button>
          <h4 className="modal-title">
            {rowIsDisabled ? 'Просмотр' : this.props.title}
          </h4>
        </div>
        <div className="modal-body">
          {this.getVisibleColumns(columns).map((column: TableColum) => {
            const { field, extra, isIcon } = column;

            return (
              !this.isColumnSpecial(column) && (
                <div className="form-group" key={field}>
                  <div>
                    <label
                      className={cn(`form-group ${extra?.tooltip && field}`, {
                        required: this.isColumnRequired(column),
                      })}
                      dangerouslySetInnerHTML={{
                        __html: `${column.contentNumb ?? ''} ${column.name}`,
                      }}
                    />
                    {isIcon && (
                      <span>
                        {isIcon && (
                          // @ts-ignore
                          // TODO !!!
                          <Tooltip target=".pi-file" />
                        )}
                        <span
                          className="pi pi-file"
                          data-pr-tooltip="No notifications"
                          data-pr-position="right"
                          data-pr-at="right+5 top"
                          // onClick={this.getPreFilledForm}
                        ></span>
                      </span>
                    )}
                  </div>
                  {this.createControl(column)}
                  {this.state.validateState[field] && (
                    <span className="label label-danger">
                      {this.state.validateState[field]}
                    </span>
                  )}
                  {this.state.validateStateSuccess[field] && (
                    <div className="label label-success">
                      {this.state.validateStateSuccess[column.field]}
                    </div>
                  )}
                </div>
              )
            );
          })}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={onModalClose}
          >
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={this.onClick}
            disabled={this.props.disabled || this.state.isFormLocked}
          >
            Сохранить
          </button>
        </div>
      </div>
    );
  }
}

export default EditorModalTable;

// =(
